import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Comlink from 'comlink';
import { BehaviorSubject, firstValueFrom, catchError, of } from 'rxjs';
import { VirtualBackgroundType } from '../common/interfaces/rtc-interface';
import { VBWebGLWorker } from '../web-workers/vb_webgl.worker';
import { IconBackground } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { ERRORS } from '../common/utils/notification-constants';
import { intercomArticles } from '../intercom-articles';
import { SpaceRepository } from '../state/space.repository';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { VIDEO_STREAM_CAPTURE_WIDTH, VIDEO_STREAM_CAPTURE_HEIGHT } from '../models/device-manger';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { TelemetryService } from './telemetry.service';
import {
  DEFAULT_NOTIFICAION_TIMEOUT,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  NotificationActionButton,
  V2NotificationDataBuilder,
  NotificationIconConfig,
} from './notification-toaster.service';
import { GpuDetectorService } from './gpu-detector.service';
import { FlagsService } from './flags.service';
import { CustomerSupportService } from './customer-support.service';
import { ResourcesService } from './resources.service';
import { VirtualBackgroundUtilsService } from './virtual-background/virtual-background-utils.service';
import { ItemsCanvasService } from './items-canvas.service';

declare let MediaStreamTrackGenerator: any;
declare let MediaStreamTrackProcessor: any;

@Injectable({
  providedIn: 'root',
})
export class VirtualBackgroundInsertableStreamService {
  private readonly VIRTUAL_BACKGROUND_TYPE = 'VIRTUAL_BACKGROUND_TYPE';
  private readonly VIRTUAL_BACKGROUND_IMAGE = 'VIRTUAL_BACKGROUND_IMAGE';
  private readonly DEFAULT_MAX_TIME_TO_APPLY_EFFECT_IN_SECS = 15;
  private readonly APPLY_VB_TIMEOUT_ERROR = 'apply virtual background timeout';

  private _virtualBackgroundType: VirtualBackgroundType = VirtualBackgroundType.NONE;
  private _lastBackgroundImageSource: string | undefined;
  private _vbWorkerWrapper?: Comlink.Remote<VBWebGLWorker>;
  private _worker?: Worker;
  private _sourceVideoTrack: MediaStreamTrack | null = null;
  private _generatedVideoTrack: MediaStreamTrack | null = null;
  private _onVbProcessingError?: () => void;
  private _maxTimeToApplyEffectInSecs =
    (this.flagsService.featureFlagsVariables.spaces_virtual_backgrounds
      ?.maxTimeToApplyEffectInSecs as number) || this.DEFAULT_MAX_TIME_TO_APPLY_EFFECT_IN_SECS;

  private _openPanelCount = 0;
  private readonly _vbErrorOccurred$ = new BehaviorSubject<boolean>(false);
  readonly vbErrorOccurred$ = this._vbErrorOccurred$.asObservable();

  notifyPanelOpened(): void {
    this._openPanelCount++;
  }

  notifyPanelClosed(): void {
    this._openPanelCount = Math.max(0, this._openPanelCount - 1);
    if (this._openPanelCount === 0) {
      this._vbErrorOccurred$.next(false);
    }
  }

  constructor(
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private telemetry: TelemetryService,
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
    private gpuDetectorService: GpuDetectorService,
    private flagsService: FlagsService,
    private customerSupportService: CustomerSupportService,
    private resourcesService: ResourcesService,
    private vbUtilsService: VirtualBackgroundUtilsService,
    private spaceRepo: SpaceRepository,
    private itemsCanvasService: ItemsCanvasService,
  ) {
    this.loadVirtualBackgroundStateFromLocalStorage();
    this.logVirtualBackgroundState();
  }

  /**
   * Validates whether a virtual background image exists and is accessible.
   * For unrestricted institutions, always returns true.
   * For restricted institutions, checks if the image exists in the institution's virtual background folder.
   * @param src The image source URL to validate
   * @returns Promise<boolean> indicating if the image exists and is accessible
   */
  private async _validateImageExists(src: string): Promise<boolean> {
    try {
      const institution = this.spaceRepo.activeSpaceId
        ? this.spaceRepo.getSpace(this.spaceRepo.activeSpaceId)?.institution
        : undefined;
      if (!this.vbUtilsService.isInstitutionVBsRestricted(institution)) {
        return true;
      }
      const folderId = this.vbUtilsService.getInstitutionVBsFolder(institution);
      if (!folderId) {
        return false;
      }
      const folder = await firstValueFrom(
        this.resourcesService.getPublicGuestFolder(folderId).pipe(catchError(() => of(null))),
      );
      return (
        folder?.resources?.files?.some(
          (resource) => resource.url?.split('?')[0] === src.split('?')[0],
        ) ?? false
      );
    } catch {
      return false;
    }
  }

  set virtualBackgroundType(virtualBackgroundType: VirtualBackgroundType) {
    this._virtualBackgroundType = virtualBackgroundType;
    localStorage.setItem(this.VIRTUAL_BACKGROUND_TYPE, this._virtualBackgroundType);
    this.logVirtualBackgroundState();
  }

  set lastBackgroundImageSource(backgroundImageSource: string | undefined) {
    if (backgroundImageSource) {
      this._lastBackgroundImageSource = backgroundImageSource;
      localStorage.setItem(this.VIRTUAL_BACKGROUND_IMAGE, this._lastBackgroundImageSource);
    }
  }

  get lastBackgroundImageSource(): string | undefined {
    return this._lastBackgroundImageSource;
  }

  get virtualBackgroundType(): VirtualBackgroundType {
    return this._virtualBackgroundType;
  }

  get sourceTrack(): MediaStreamTrack | null {
    return this._sourceVideoTrack;
  }

  async applyVirtualBackgroundEffect(videoStream: MediaStream) {
    if (this._lastBackgroundImageSource) {
      try {
        const imageExists = await this._validateImageExists(this._lastBackgroundImageSource);
        if (!imageExists) {
          this.virtualBackgroundType = VirtualBackgroundType.NONE;
          this._lastBackgroundImageSource = undefined;
          localStorage.removeItem(this.VIRTUAL_BACKGROUND_IMAGE);
          return videoStream;
        }
        // A cached inst/Pencil VB URL is a signed URL that may have expired while
        // in localStorage (e.g. on rejoin), so re-sign it to refresh the token.
        // Unsigned custom uploads (no Expires) don't need it and are left as-is.
        if (new URL(this._lastBackgroundImageSource).searchParams.has('Expires')) {
          this._lastBackgroundImageSource = await this.itemsCanvasService.getTrustedUrl(
            this._lastBackgroundImageSource,
          );
        }
      } catch (error) {
        console.error('[VB] Error during validation:', error);
      }
    }
    await this.stopVirtualBackgroundProcessing();
    try {
      let timeoutId: NodeJS.Timer | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = modifiedSetTimeout(() => {
          reject(new Error(this.APPLY_VB_TIMEOUT_ERROR));
        }, this._maxTimeToApplyEffectInSecs * 1000);
      });

      const applyEffect = this.applyVbEffectThrowWebWorker(videoStream);

      const result = await Promise.race([applyEffect, timeoutPromise]);
      if (timeoutId) {
        clearTimeout(timeoutId); // Clear the timeout if applyEffect finishes first
      }
      this._vbErrorOccurred$.next(false);
      return result;
    } catch (e) {
      await this.stopVirtualBackgroundProcessing();
      const errorMessage = e instanceof Error ? e.message : JSON.stringify(e);
      if (errorMessage === this.APPLY_VB_TIMEOUT_ERROR) {
        // if a timeout happen then most probably the worker terminated so we will create a new one when needed again
        this._worker?.terminate();
        this._vbWorkerWrapper = undefined;
      }
      this.handleVirtualBackgroundError(errorMessage);
      throw e;
    }
  }

  async stopVirtualBackgroundProcessing() {
    this._generatedVideoTrack?.stop();
    this._sourceVideoTrack?.stop();
    this._generatedVideoTrack = null;
    this._sourceVideoTrack = null;
    // For the transferable track path (Safari 18+ / Firefox), the worker holds its own
    // cloned track which must be stopped explicitly. No-op when not using that path.
    await this._vbWorkerWrapper?.cleanupTransferableTrack();
  }

  isVirtualBackgroundMode() {
    return this._virtualBackgroundType !== VirtualBackgroundType.NONE;
  }

  private isMainThreadInsertableStreamsSupported(): boolean {
    return 'MediaStreamTrackProcessor' in window && 'MediaStreamTrackGenerator' in window;
  }

  isBrowserSupported() {
    // On iOS, all browsers use WebKit under the hood, so even Chrome/Edge on iPhone
    // must go through the worker-based transferable track path — not the main-thread path.
    if (this.deviceAndBrowserDetectorService.isiOS()) {
      const iosVersion = this.deviceAndBrowserDetectorService.getIOSMajorVersion();
      return iosVersion !== null && iosVersion >= 18;
    }
    // Chrome/Edge desktop: MediaStreamTrackProcessor + MediaStreamTrackGenerator in window context
    if (this.isMainThreadInsertableStreamsSupported()) {
      return true;
    }
    // Safari 18+ desktop: VideoTrackGenerator + MediaStreamTrackProcessor in dedicated workers.
    // Firefox desktop is excluded — it cannot postMessage MediaStreamTrack (neither transfer nor clone).
    const safariVersion = this.deviceAndBrowserDetectorService.getSafariMajorVersion();
    return safariVersion !== null && safariVersion >= 18;
  }

  isDeviceSupported() {
    // Mirror NSFW's canDeviceSpecsRunNsfw(): only block non-desktop devices WITH major GPU caveat.
    // Tablets and mobile devices with decent GPU can run VB (same as NSFW allows them).
    const isNonDesktop = !this.deviceAndBrowserDetectorService.isDesktop();
    const hasMajorPerformanceCaveat = this.gpuDetectorService.hasMajorPerformanceCaveat();
    const supportsFloatTextures = this.gpuDetectorService.supportsFloatTextures();
    if (isNonDesktop && hasMajorPerformanceCaveat) {
      return false;
    }
    // MediaPipe's GPU-delegated segmentation postprocessor requires renderable float32
    // or float16 RGBA textures. Some Android GPUs lack this, causing a fatal WASM abort.
    if (!supportsFloatTextures) {
      return false;
    }
    return true;
  }

  isVirtualBackgroundSupported() {
    return this.isBrowserSupported() && this.isDeviceSupported();
  }

  private async applyVbEffectThrowWebWorker(videoStream: MediaStream) {
    if (this.virtualBackgroundType == VirtualBackgroundType.NONE) {
      return videoStream;
    }
    this._sourceVideoTrack = videoStream.getVideoTracks()[0].clone();

    if (!this._vbWorkerWrapper) {
      this._worker = new Worker(new URL('../web-workers/vb_webgl.worker', import.meta.url));
      const vbWebGLWorker = Comlink.wrap<typeof VBWebGLWorker>(this._worker);
      this._vbWorkerWrapper = await new vbWebGLWorker();
    }

    if (!this._sourceVideoTrack) {
      return videoStream;
    }

    const worker = this._vbWorkerWrapper;
    worker.setCallerErrorCallback(
      Comlink.proxy((errorMessage: string) => {
        if (this._onVbProcessingError) {
          this._onVbProcessingError();
        }
        this.handleVirtualBackgroundError(
          `failture in the transform stream function: ${errorMessage}`,
        );
      }),
    );

    const bgSrc =
      this._virtualBackgroundType == 'background-image' ? this._lastBackgroundImageSource : null;

    // Use a temporary <video> element to get the true displayed dimensions for
    // both paths. The video element applies rotation metadata automatically, so
    // videoWidth/videoHeight always reflect the correct orientation — unlike
    // getSettings() which is unreliable on iOS Safari, and unlike raw frame
    // dimensions from MediaStreamTrackProcessor.
    const { width: trackWidth, height: trackHeight } = await this.getVideoElementDimensions(
      this._sourceVideoTrack,
    );

    if (!this._sourceVideoTrack) {
      return videoStream;
    }

    await worker.setupCanvas(trackWidth, trackHeight);

    if (
      !this.deviceAndBrowserDetectorService.isiOS() &&
      this.isMainThreadInsertableStreamsSupported()
    ) {
      await worker.ensureModelLoaded();

      if (!this._sourceVideoTrack) {
        return videoStream;
      }

      const processedStream = new MediaStream();
      const trackProcessor = new MediaStreamTrackProcessor({ track: this._sourceVideoTrack });
      const trackGenerator = new MediaStreamTrackGenerator({ kind: 'video' });
      this._generatedVideoTrack = trackGenerator;
      const readable = trackProcessor.readable as any;
      const writable = trackGenerator.writable as any;
      await worker.applyBackgroundEffect(
        Comlink.transfer(readable, [readable]),
        Comlink.transfer(writable, [writable]),
        bgSrc,
      );
      processedStream.addTrack(trackGenerator);
      return processedStream;
    } else {
      const generatedTrack = await worker.processTransferableTrack(
        Comlink.transfer(this._sourceVideoTrack, [this._sourceVideoTrack as any]),
        bgSrc,
      );
      this._generatedVideoTrack = generatedTrack;
      const processedStream = new MediaStream();
      processedStream.addTrack(generatedTrack);
      return processedStream;
    }
  }

  private getVideoElementDimensions(
    track: MediaStreamTrack,
  ): Promise<{ width: number; height: number }> {
    const TIMEOUT_MS = 3000;
    const fallback = { width: VIDEO_STREAM_CAPTURE_WIDTH, height: VIDEO_STREAM_CAPTURE_HEIGHT };

    return new Promise((resolve) => {
      if (track.readyState === 'ended') {
        resolve(fallback);
        return;
      }

      const clonedTrack = track.clone();
      const video = document.createElement('video');
      video.srcObject = new MediaStream([clonedTrack]);
      video.muted = true;
      video.playsInline = true;
      // Safari requires the element in the DOM for reliable metadata events
      // on MediaStream sources. Position off-screen to avoid visual artifacts.
      video.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;';
      document.body.appendChild(video);

      let resolved = false;

      const resolveWith = (width: number, height: number) => {
        if (resolved) {
          return;
        }
        resolved = true;
        clearTimeout(timeoutId);
        video.pause();
        video.srcObject = null;
        clonedTrack.stop();
        video.remove();
        resolve({ width, height });
      };

      video.onloadedmetadata = () => {
        resolveWith(video.videoWidth, video.videoHeight);
      };

      clonedTrack.onended = () => {
        if (!resolved) {
          resolveWith(fallback.width, fallback.height);
        }
      };

      const timeoutId = modifiedSetTimeout(
        () => {
          if (!resolved) {
            resolveWith(fallback.width, fallback.height);
          }
        },
        TIMEOUT_MS,
        true,
      );

      video.play().catch(() => {
        // play() rejection alone doesn't mean failure — loadedmetadata may
        // still fire. The timeout above is the safety net.
      });
    });
  }

  private handleVirtualBackgroundError(errorMessage: string) {
    this.telemetry.event('[Error Applying Virtual Background]', {
      virtualBackgroundError: errorMessage,
    });
    this.virtualBackgroundType = VirtualBackgroundType.NONE;
    if (this._openPanelCount > 0) {
      this._vbErrorOccurred$.next(true);
    } else {
      this.showErrorMessageForApplyingVB();
    }
  }

  set onVbProcessingError(cb: () => void) {
    this._onVbProcessingError = cb;
  }

  private loadVirtualBackgroundStateFromLocalStorage() {
    if (!this.isVirtualBackgroundSupported()) {
      // to not apply VB type on localstorage if not supported
      // this is mainly valuable when we add more constrains on who can use VB
      return;
    }
    const storedVirtualBackgroundType = localStorage.getItem(
      this.VIRTUAL_BACKGROUND_TYPE,
    ) as VirtualBackgroundType;
    if (storedVirtualBackgroundType) {
      this._virtualBackgroundType = storedVirtualBackgroundType;
    }
    const storedLastBackgroundImageSource = localStorage.getItem(this.VIRTUAL_BACKGROUND_IMAGE);
    if (storedLastBackgroundImageSource) {
      this._lastBackgroundImageSource = storedLastBackgroundImageSource;
    }
  }

  private showErrorMessageForApplyingVB() {
    const notificationTitle = this.translateService.instant('Could not start virtual background');
    const notificationIcon: NotificationIconConfig = {
      assetName: 'cam_off_notification_icon',
      type: NotificationIconTypes.SVG_ICON,
      background: IconBackground.ERROR,
    };

    const notitificationDescription = this.translateService.instant(
      'We’ve turned off your camera for now. Please refresh the page and re-enable virtual background in settings.',
    );

    const notificationLinkButton: NotificationActionButton = {
      text: this.translateService.instant('Learn More'),
      onClick: () => {
        this.customerSupportService.openLink(intercomArticles.VirtualBackgroundSupport);
      },
      close: false,
    };

    const notificationData = new V2NotificationDataBuilder(ERRORS.FAILED_APPLYING_VB)
      .style(ToasterPopupStyle.WARN)
      .type(NotificationType.WARNING)
      .title(notificationTitle)
      .description(notitificationDescription)
      .icon(notificationIcon)
      .actionButtonsConfig({
        link: notificationLinkButton,
      })
      .timeOut(DEFAULT_NOTIFICAION_TIMEOUT)
      .dismissable(true)
      .showProgressBar(true)
      .onActivateTick(true)
      .build();

    this.notificationToasterService.showNotification(notificationData);
  }

  private logVirtualBackgroundState() {
    this.telemetry.setSessionVars({
      virtual_background_chosen: this.isVirtualBackgroundMode(),
    });
  }
}
